import { createContext } from "react";
import { request } from "../lib/api";

/** Prepared work is a draft, never evidence of an accepted Agent turn. */
export interface SurfaceIntentDraft {
  command_id: string;
  panel_key: string;
  block: number;
  session_id: string;
  text: string;
  object?: { kind: string; id: string };
  read_key?: string;
  effect_id?: string;
  obligation_id?: string;
  scope: Record<string, unknown>;
  skill?: string;
  reference?: Record<string, unknown>;
}

/** Page references are JSON; object member order does not change what they name. */
function ordered(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(ordered);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value)
    .sort(([left], [right]) => left.localeCompare(right)).map(([key, item]) => [key, ordered(item)]));
  return value;
}

/** Coordinates for unsent words, independent of a press's temporary receipts. */
export function surfaceDraftKey(draft: SurfaceIntentDraft): string {
  return JSON.stringify([draft.panel_key, draft.block, draft.skill ?? null,
    draft.object?.kind ?? null, draft.object?.id ?? null, ordered(draft.reference ?? null),
    draft.reference?.purpose === "new_task" ? null : draft.text]);
}

/** The Owner shell places this draft in its existing conversation composer. */
export const SurfaceIntentComposer = createContext<((draft: SurfaceIntentDraft) => void) | null>(null);
export const SurfaceConversationFocus = createContext<(() => void) | null>(null);

export function prepareSurfaceIntent(panel: string, block: number, params: Record<string, unknown>) {
  return request<SurfaceIntentDraft>(`/api/panels/${encodeURIComponent(panel)}/surface-intents/prepare`, {
    method: "POST", body: JSON.stringify({ ...params, block }),
  });
}

/**
 * Call only from the Owner's visible composer submit, never a guest callback.
 *
 * Sent under the name it was prepared with; sending it again after an answer
 * that did not come back is the same request, answered with its first receipt.
 */
export function submitSurfaceIntent(draft: SurfaceIntentDraft, text: string) {
  return request<{ command_id: string; obligation_id: string; session_id: string }>(
    `/api/panels/${encodeURIComponent(draft.panel_key)}/surface-intents`, {
      method: "POST", body: JSON.stringify({ ...draft, text }),
    });
}
